import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import apiService from '../services/api';

const ChatSidebar = ({
    selectedSessionId,
    onSessionSelect,
    onNewChat,
    selectedCountry,
    refreshTrigger = 0,
    collapsed = false,
    onToggleCollapse
}) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Load chat sessions
    useEffect(() => {
        loadChatSessions();
    }, [refreshTrigger]);

    const loadChatSessions = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await apiService.getChatSessions();
            setSessions(response.data.sessions || []);
        } catch (err) {
            console.error('Error loading chat sessions:', err);
            setError('Failed to load chat sessions');
        } finally {
            setLoading(false);
        }
    };

    const formatSessionDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    const getSessionTitle = (session) => {
        // Use the LLM-generated title if available, otherwise show "New Chat"
        return session.title || "New Chat";
    };

    if (loading) {
        return (
            <Paper sx={{ width: collapsed ? 60 : 350, height: '100%', p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress size={collapsed ? 24 : 40} />
                </Box>
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                width: collapsed ? 60 : 350,
                minWidth: collapsed ? 60 : 350,
                height: '100%',
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out'
            }}
        >
            {/* Header */}
            <Box sx={{ p: collapsed ? 1 : 2, pb: 1, flexShrink: 0 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'space-between',
                    mb: collapsed ? 0 : 2,
                    flexDirection: collapsed ? 'column' : 'row',
                    gap: collapsed ? 1 : 0
                }}>
                    {!collapsed && (
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <ChatIcon sx={{ mr: 1 }} />
                            Chat History
                        </Typography>
                    )}
                    {collapsed && (
                        <Tooltip title="Chat History">
                            <ChatIcon color="primary" />
                        </Tooltip>
                    )}
                    <Tooltip title="Start a new chat">
                        <IconButton
                            onClick={onNewChat}
                            color="primary"
                            size="small"
                        >
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
                        <IconButton
                            onClick={onToggleCollapse}
                            size="small"
                            sx={{ mt: collapsed ? 1 : 0 }}
                        >
                            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!collapsed && <Divider />}

            {/* Error Message */}
            {!collapsed && error && (
                <Box sx={{ p: 2, flexShrink: 0 }}>
                    <Alert severity="error" size="small">
                        {error}
                    </Alert>
                </Box>
            )}

            {/* Sessions List - Scrollable Area */}
            {!collapsed && (
            <Box sx={{
                flexGrow: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: 'calc(100vh - 480px)' // Match the chat messages container height
            }}>
                {sessions.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">
                            No chat sessions yet.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Start a new chat to begin!
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{
                        overflow: 'auto',
                        flexGrow: 1,
                        maxHeight: '100%'
                    }}>
                        <List dense sx={{ p: 0, pt: 1 }}>
                            {sessions.map((session) => (
                                <ListItem key={session.id} disablePadding>
                                    <ListItemButton
                                        selected={selectedSessionId === session.id}
                                        onClick={() => onSessionSelect(session.id)}
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            mx: 1, // Add horizontal margin for contained look
                                            borderRadius: 2, // Rounded corners
                                            '&.Mui-selected': {
                                                backgroundColor: 'primary.light',
                                                color: 'primary.contrastText',
                                                '&:hover': {
                                                    backgroundColor: 'primary.main',
                                                }
                                            },
                                            '&:hover': {
                                                borderRadius: 2, // Maintain rounded corners on hover
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" noWrap>
                                                    {getSessionTitle(session)}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <Chip
                                                        label={session.country}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            height: '20px',
                                                            color: selectedSessionId === session.id ? 'inherit' : 'text.secondary'
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: selectedSessionId === session.id ? 'inherit' : 'text.secondary',
                                                            fontSize: '0.7rem'
                                                        }}
                                                    >
                                                        {formatSessionDate(session.created_at)}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </Box>
            )}

            {/* Footer info */}
            {!collapsed && (
                <>
                    <Divider />
                    <Box sx={{ p: 2, pt: 1, flexShrink: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {sessions.length} chat session{sessions.length !== 1 ? 's' : ''}
                        </Typography>
                        {selectedCountry && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Current: {selectedCountry}
                            </Typography>
                        )}
                    </Box>
                </>
            )}
        </Paper>
    );
};

export default ChatSidebar; 